import { template as renderTemplate } from 'underscore';
import $ from '../../../../../../FrontendBundle/Resources/js/jquery';
import CombineItemsCommand from '../../command/combineItemsCommand';
import FlipItemCommand from '../../command/flipItemCommand';
import GroupItemsCommand from '../../command/groupItemsCommand';
import UnCombineItemCommand from '../../command/unCombineItemCommand';
import UnGroupItemCommand from '../../command/unGroupItemCommand';
import AbstractGroupItem from '../../item/abstractGroupItem';
import CircleItem from '../../item/circleItem';
import CombinedGroupItem from '../../item/combinedGroupItem';
import GroupItem from '../../item/groupItem';
import RectangleItem from '../../item/rectangleItem';

/**
 * @class ContextMenuOverlay
 */
export default class ContextMenuOverlay {
    element = '#editor';
    parent = '.editor-wrapper';
    contextTemplate = '#contextmenu-tpl';

    /**
     * @type {History}
     */
    history = null;

    /**
     * @type {Clipboard}
     */
    clipboard = null;

    /**
     * @type {SelectionCollection}
     */
    selection = null;

    /**
     * @type {Ofcs.Editor.Svg.Canvas}
     */
    canvas = null;

    /**
     * @type {ItemOverlappingCollisionChecker}
     */
    overlapChecker = null;

    /**
     * @param {Ofcs.Editor.Svg.Canvas} canvas
     * @param {History} history
     * @param {Clipboard} clipboard
     * @param {SelectionCollection} selection
     * @param {ItemOverlappingCollisionChecker} overlapChecker
     */
    constructor(canvas, history, clipboard, selection, overlapChecker) {
        this.canvas = canvas;
        this.history = history;
        this.clipboard = clipboard;
        this.selection = selection;
        this.overlapChecker = overlapChecker;
    }

    init() {
        const templateElement = $(this.contextTemplate);

        if (!templateElement.length) return;

        const template = renderTemplate($(templateElement).html());
        const contextMenu = template();
        const movedTemplate = $(contextMenu).appendTo(this.parent);

        $(this.element).contextmenu({
            target: movedTemplate,
            before: () => {
                this.setStatus(movedTemplate);
            },
            onItem: (context, e) => {
                const $target = $(e.target);
                const action = $target.attr('class');
                const isDisabled = $target.hasClass('disabled');

                if (isDisabled) return false;

                this.executeAction(action);
                return true;
            },
        });
    }

    setStatus(template) {
        const flipCommand = this.createFlipCommand();

        template.find('.copy, .cut')
            .toggleClass('disabled', this.selection.length === 0);

        template.find('.paste').toggleClass('disabled', !this.clipboard.hasItems());
        this.clipboard.on('change', (hasItems) => {
            $('#contextMenu').find('.paste').toggleClass('disabled', !hasItems);
        });

        template.find('.flip')
            .toggleClass('disabled', this.selection.length < 1 || !flipCommand.isValid());

        const selectionIsGroupable = this.selection.length > 1;
        const selectionIsUngroupable = this.selection.length === 1 && this.selection.first() instanceof GroupItem;

        template.find('.group')
            .toggleClass('disabled', !selectionIsGroupable && !selectionIsUngroupable)
            .toggle(!selectionIsUngroupable);

        template.find('.ungroup').toggle(selectionIsUngroupable);

        const flatSelectedItems = this.selection
            .toArray()
            .map(item => (item instanceof AbstractGroupItem ? item.resolveGroupItems() : [item]))
            .reduce((arr, items) => arr.concat(items), []);

        const combineableTypes = [RectangleItem, CircleItem, AbstractGroupItem];
        const forbiddenCombineItems = flatSelectedItems
            .filter(item => !combineableTypes.find(type => item instanceof type));

        const isSelectionValid = this.selection.length >= 2
            && forbiddenCombineItems.length === 0
            && this.overlapChecker.check(this.selection.toArray());

        const isSelectionCombinedGroup = this.selection.length === 1
            && this.selection.first() instanceof CombinedGroupItem;

        template.find('.combine')
            .toggleClass('disabled', !isSelectionValid && !isSelectionCombinedGroup)
            .toggle(!isSelectionCombinedGroup);

        template.find('.uncombine').toggle(isSelectionCombinedGroup);
    }

    createFlipCommand() {
        return new FlipItemCommand(this.selection.toArray());
    }

    executeFlipCommand() {
        const command = this.createFlipCommand();

        if (command.isValid()) {
            this.history.addAndExecuteCommand(command);
        }
    }

    executeGroupCommand() {
        const history = this.canvas.getHistory();
        const items = this.canvas.getCurrentContext().getItems();
        let command;

        if (this.selection.length === 1 && this.selection.first() instanceof GroupItem) {
            command = new UnGroupItemCommand(
                items,
                this.selection,
                this.selection.first(),
            );
        } else {
            command = new GroupItemsCommand(
                items,
                this.selection,
                this.selection.toArray(),
            );
        }

        history.addAndExecuteCommand(command);
    }

    executeCombineCommand() {
        const history = this.canvas.getHistory();
        const items = this.canvas.getCurrentContext().getItems();
        let command;

        if (this.selection.length === 1 && this.selection.first() instanceof CombinedGroupItem) {
            command = new UnCombineItemCommand(
                items,
                this.selection,
                this.selection.first(),
            );
        } else {
            command = new CombineItemsCommand(
                items,
                this.selection,
                this.selection.toArray(),
            );
        }

        history.addAndExecuteCommand(command);
    }

    executeAction(action) {
        if (!action) return;

        switch (action) {
            case 'undo':
                this.history.undo();
                break;

            case 'redo':
                this.history.redo();
                break;

            case 'copy':
                this.clipboard.copy();
                break;

            case 'cut':
                this.clipboard.cut();
                break;

            case 'paste':
                this.clipboard.paste();
                break;

            case 'flip':
                this.executeFlipCommand();
                break;

            case 'group':
            case 'ungroup':
                this.executeGroupCommand();
                break;

            case 'combine':
            case 'uncombine':
                this.executeCombineCommand();
                break;

            default:
                break;
        }
    }
}
